import React from 'react'
import MetricCard from '../../ui/MetricCard'
import DataTable from '../../ui/DataTable'
import { OPERATOR_DISPLAY_ORDER, getOperatorInfo } from './constants'

const KAMPerOperatorView = ({ groupedData }) => {
  // Get ordered operators (only those with data)
  const orderedOperators = OPERATOR_DISPLAY_ORDER
    .filter(code => groupedData[code])

  const renderOperatorTable = (operatorData) => {
    const { code, name, color, kams, totalGoal, totalGoalTarget, totalSales } = operatorData
    
    if (!Array.isArray(kams) || kams.length === 0) {
      return (
        <div key={code} className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <div 
              className="px-3 py-1 rounded-full text-white text-sm font-medium mr-3"
              style={{ backgroundColor: color }}
            >
              {name}
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Cele Sprzedażowe KAM-ów</h3>
          </div>
          <p className="text-gray-500">Brak danych performance dla KAM-ów {name}</p>
        </div>
      )
    }

    // Calculate summary metrics — the synthetic "Nieprzypisane" row is not a KAM,
    // so it must not dilute averages, the on-track ratio, or visit sums
    const realKams = kams.filter(kam => !kam.is_unassigned)
    const totalVisitsMonth = realKams.reduce((sum, kam) => sum + (kam.total_visits_month || 0), 0)
    const totalUniqueSalons = realKams.reduce((sum, kam) => sum + (kam.unique_salons_month || 0), 0)
    const avgForecast = realKams.length > 0 ?
      realKams.reduce((sum, kam) => sum + (kam.forecast_vs_goal_percent ?? kam.achievement_percent ?? 0), 0) / realKams.length : 0
    const kamsOnTrack = realKams.filter(kam => (kam.forecast_vs_goal_percent ?? kam.achievement_percent ?? 0) >= 80).length

    const hasTarget = totalGoalTarget > 0 && totalGoalTarget !== totalGoal

    const tableRows = kams.map((kam, index) => ({
      id: index,
      kam_name: kam.kam_name || 'N/A',
      actual_sales_mtd: kam.actual_sales_mtd || 0,
      goal_value: kam.goal_value || 0,
      goal_value_target: kam.goal_value_target || null,
      achievement_mtd: kam.achievement_percent || 0,
      last_7_days_sales: kam.last_7_days_sales || 0,
      working_days_remaining: kam.working_days_remaining || 0,
      forecast_eom: kam.forecast_vs_goal_percent || null,
      visits_month: kam.total_visits_month || 0,
      unique_salons_month: kam.unique_salons_month || 0,
      completed_visits_month: kam.completed_visits_month || 0,
      total_visits_alltime: kam.total_visits_alltime || 0
    }))

    const getPercentageColor = (value) => {
      if (value >= 100) return 'text-green-700 bg-green-50'
      if (value >= 80) return 'text-blue-700 bg-blue-50'
      if (value >= 60) return 'text-orange-700 bg-orange-50'
      return 'text-red-700 bg-red-50'
    }

    return (
      <div key={code} className="bg-white rounded-lg shadow p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center mb-4">
          <div 
            className="px-3 py-1 rounded-full text-white text-sm font-medium mr-3"
            style={{ backgroundColor: color }}
          >
            {name}
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Performance KAM-ów</h3>
        </div>

        {/* Summary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <MetricCard
            title="Łączna Sprzedaż MTD"
            value={hasTarget
              ? `${totalSales.toLocaleString()} / ${totalGoal.toLocaleString()} (${totalGoalTarget.toLocaleString()})`
              : `${totalSales.toLocaleString()} / ${totalGoal.toLocaleString()}`
            }
            icon={() => <i className="fa-solid fa-bullseye text-blue-600"></i>}
          />
          <MetricCard
            title="Wizyty / Salony"
            value={`${totalVisitsMonth} / ${totalUniqueSalons}`}
            icon={() => <i className="fa-solid fa-arrow-trend-up text-green-600"></i>}
          />
          <MetricCard
            title="Śr. Prognoza EOM"
            value={`${avgForecast.toFixed(1)}%`}
            icon={() => <i className="fa-solid fa-trophy text-yellow-600"></i>}
          />
          <MetricCard
            title="KAMowie na ścieżce (≥80%)"
            value={`${kamsOnTrack}/${realKams.length}`}
            icon={() => <i className="fa-solid fa-medal text-purple-600"></i>}
          />
        </div>

        {/* Performance Table */}
        <DataTable
          rows={tableRows}
          columns={[
            {
              field: 'kam_name',
              headerName: 'KAM',
              flex: 2,
              minWidth: 150,
              type: 'string',
            },
            {
              field: 'goal_value',
              headerName: 'Cel MIN',
              flex: 0.9,
              minWidth: 85,
              type: 'number',
              align: 'right',
              headerAlign: 'right',
              renderCell: (params) => (
                <div className="font-semibold text-blue-900">
                  {params.value.toLocaleString()}
                </div>
              ),
            },
            {
              field: 'goal_value_target',
              headerName: 'Cel TARGET',
              flex: 0.9,
              minWidth: 90,
              type: 'number',
              align: 'right',
              headerAlign: 'right',
              renderCell: (params) => {
                const val = params.value || params.row.goal_value || 0
                return <div className="font-semibold text-blue-700">{val.toLocaleString()}</div>
              },
            },
            {
              field: 'actual_sales_mtd',
              headerName: 'Sprzedaż MTD',
              flex: 1,
              minWidth: 95,
              type: 'number',
              align: 'right',
              headerAlign: 'right',
              renderCell: (params) => (
                <div className="font-semibold text-gray-900">
                  {params.value.toLocaleString()}
                </div>
              ),
            },
            {
              field: 'achievement_mtd',
              headerName: 'MTD %',
              flex: 1,
              minWidth: 90,
              type: 'number',
              align: 'right',
              headerAlign: 'right',
              renderCell: (params) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPercentageColor(params.value)}`}>
                  {params.value.toFixed(1)}%
                </span>
              ),
            },
            {
              field: 'last_7_days_sales',
              headerName: 'Ostatnie 7 dni',
              flex: 1.1,
              minWidth: 105,
              type: 'number',
              align: 'right',
              headerAlign: 'right',
              renderCell: (params) => (
                <div className="text-right">
                  <div className="font-semibold text-gray-900">{params.value.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">({params.row.working_days_remaining} dni)</div>
                </div>
              ),
            },
            {
              field: 'forecast_eom',
              headerName: 'Prognoza EOM',
              flex: 1.2,
              minWidth: 120,
              type: 'number',
              align: 'right',
              headerAlign: 'right',
              renderCell: (params) => {
                const value = params.value
                if (value === null || value === undefined) {
                  return <span className="text-gray-500 text-xs">-</span>
                }
                return (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPercentageColor(value)}`}>
                    <i className="fa-solid fa-bullseye mr-1"></i>
                    {value.toFixed(1)}%
                  </span>
                )
              },
            },
            {
              field: 'visits_month',
              headerName: 'Wizyty w miesiącu',
              flex: 1.3,
              minWidth: 130,
              type: 'number',
              align: 'right',
              headerAlign: 'right',
              renderCell: (params) => (
                <div className="flex items-center justify-end">
                  <i className="fa-solid fa-bullseye text-green-600 mr-1"></i>
                  <span>{params.value}</span>
                </div>
              ),
            },
            {
              field: 'unique_salons_month',
              headerName: 'Unikalne Salony',
              flex: 1.3,
              minWidth: 130,
              type: 'number',
              align: 'right',
              headerAlign: 'right',
              renderCell: (params) => (
                <div className="flex items-center justify-end">
                  <i className="fa-solid fa-arrow-trend-up text-blue-600 mr-1"></i>
                  <span>{params.value}</span>
                </div>
              ),
            },
            {
              field: 'completed_visits_month',
              headerName: 'Ukończone w miesiącu',
              flex: 1.3,
              minWidth: 140,
              type: 'number',
            },
            {
              field: 'total_visits_alltime',
              headerName: 'Wizyty Ogółem',
              flex: 1.2,
              minWidth: 120,
              type: 'number',
            },
          ]}
          pageSize={10}
          autoHeight
          sortModel={[{ field: 'forecast_eom', sort: 'desc' }]}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">
        <i className="fa-solid fa-bullseye mr-2"></i>
        Performance per Operator
      </h3>
      {orderedOperators.map(opCode => {
        const operatorData = groupedData[opCode]
        if (!operatorData) return null
        return renderOperatorTable(operatorData)
      })}
    </div>
  )
}

export default KAMPerOperatorView


